import { DatePickerProvider, useDatePickerContext } from '@rehookify/datepicker'
import type { ReactNode } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import { useMediaQueryContext } from '../../providers/MediaQueryProvider'
import * as S from './Calendar.styled'
import type { CalendarType } from './Calendar.types'
import { CalendarMonth } from './CalendarMonth'
import { CalendarTimeInput } from './CalendarTimeInput'
import { useTime } from './hooks/useTime'
import { getInitialSelectedTimes } from './utils/getInitialSelectedTimes'
import { setTimeToDate } from './utils/setTimeToDate'

type CalendarOptions = {
  selectedTimes: string[]
  areSelectedTimesValid: boolean
}

type CalendarProps = {
  selectedDates: Date[]
  onDatesChange?: (value: Date[]) => void
  type: CalendarType
  showTimeInputs?: boolean
  minDate?: Date
  maxDate?: Date
  timeLabels?: string[]
  initialTimeValues?: string[]
  bare?: boolean
  footer?: (options: CalendarOptions) => ReactNode
  setFocusDate?: (date: Date) => void
}

type CalendarContentProps = Pick<
  CalendarProps,
  'selectedDates' | 'showTimeInputs' | 'minDate' | 'maxDate' | 'footer' | 'bare'
> & {
  selectedTimes: string[]
  setSelectedTimes: (value: string[]) => void
  timeLabels: string[]
}

const CalendarContent = ({
  selectedDates,
  showTimeInputs,
  selectedTimes,
  setSelectedTimes,
  minDate,
  maxDate,
  timeLabels,
  footer,
  bare,
}: CalendarContentProps) => {
  const {
    data: { calendars },
  } = useDatePickerContext()
  const { isValid } = useTime()

  const newDatesWithTimes = useMemo(() => {
    return selectedDates.map((date, index) => {
      const time = selectedTimes[index]

      return setTimeToDate(date, isValid(time) ? time : undefined)
    })
  }, [selectedDates, selectedTimes])

  const isMinDateValid = (date?: Date) => {
    if (!minDate || !date) return true

    return date > minDate
  }

  const isMaxDateValid = (date?: Date) => {
    if (!maxDate || !date) return true

    return date < maxDate
  }

  const isTimeValid = (time: string, index: number) => {
    if (!showTimeInputs) return true

    return (
      isValid(time) &&
      isMinDateValid(newDatesWithTimes[index]) &&
      isMaxDateValid(newDatesWithTimes[index])
    )
  }

  const areSelectedTimesValid = useMemo(() => {
    return !selectedTimes.some((time, index) => !isTimeValid(time, index))
  }, [selectedTimes])

  const handleTimeChange = (value: string, index: number) => {
    const updatedTimes = [...selectedTimes]
    updatedTimes[index] = value

    setSelectedTimes(updatedTimes)
  }

  return (
    <S.Container bare={bare}>
      <S.MonthsContainer>
        {calendars.map((calendar, index) => (
          <CalendarMonth
            key={calendar.month}
            calendar={calendar}
            showPrevMonthButton={index === 0}
            showNextMonthButton={index === calendars.length - 1}
          />
        ))}
      </S.MonthsContainer>
      {showTimeInputs && (
        <S.TimeContainer>
          {selectedTimes.map((time, index) => (
            <CalendarTimeInput
              key={index}
              value={time}
              label={timeLabels[index]}
              isValid={isTimeValid(time, index)}
              onValueChange={(value) => handleTimeChange(value, index)}
            />
          ))}
        </S.TimeContainer>
      )}
      {footer?.({ selectedTimes, areSelectedTimesValid })}
    </S.Container>
  )
}

export const Calendar = ({
  selectedDates,
  onDatesChange,
  type,
  showTimeInputs = true,
  minDate,
  maxDate,
  timeLabels,
  initialTimeValues,
  footer,
  bare,
}: CalendarProps) => {
  const media = useMediaQueryContext()
  const calendarOffsets = type === 'single' || !media.tablet ? [] : [1]
  const [selectedTimes, setSelectedTimes] = useState<string[]>(
    initialTimeValues || getInitialSelectedTimes(selectedDates, type),
  )
  const { isValid } = useTime()

  useEffect(() => {
    handleDatesChange(selectedDates)
  }, [selectedTimes])

  const handleDatesChange = (dates: Date[]) => {
    if (!showTimeInputs) {
      onDatesChange?.(dates)
    }

    const datesWithTime = dates.map((date, index) => {
      const time = selectedTimes[index] || ''

      return setTimeToDate(date, isValid(time) ? time : undefined)
    })
    onDatesChange?.(datesWithTime)
  }

  const getTimeLabels = () => {
    if (timeLabels) {
      return timeLabels
    }

    if (type === 'single') {
      return ['Time']
    }

    return ['Start time', 'End time']
  }

  return (
    <DatePickerProvider
      config={{
        selectedDates,
        onDatesChange: handleDatesChange,
        calendar: {
          offsets: calendarOffsets,
          mode: 'fluid',
        },
        dates: {
          mode: type,
          minDate,
          maxDate,
          toggle: false,
          selectSameDate: true,
        },
        locale: {
          day: 'numeric',
        },
      }}
    >
      <CalendarContent
        selectedDates={selectedDates}
        showTimeInputs={showTimeInputs}
        minDate={minDate}
        maxDate={maxDate}
        selectedTimes={selectedTimes}
        setSelectedTimes={setSelectedTimes}
        timeLabels={getTimeLabels()}
        footer={footer}
        bare={bare}
      />
    </DatePickerProvider>
  )
}
